import { useState, useEffect, useCallback, useRef } from "react";
import { useLocalization } from "@fluent/react";
import { useNotificationContext } from "cerulean";
import { DRAWER_TYPES } from "../../RecipientDrawer";

type DrawerType = typeof DRAWER_TYPES.edit | typeof DRAWER_TYPES.add;

const useWireRecipientDrawerState = (
  recipients: API.Recipient[],
  onSelectRecipient: (rowIndex: number, recipient: API.Recipient) => void
) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();

  // Keep a ref to latest recipients so handleCloseDrawer always sees fresh data
  const recipientsRef = useRef(recipients);
  recipientsRef.current = recipients;

  const [isOpen, setIsOpen] = useState(false);
  const [drawerType, setDrawerType] = useState<DrawerType>(DRAWER_TYPES.add);
  const [selectedRecipientId, setSelectedRecipientId] = useState<
    API.RecipientId | undefined
  >(undefined);
  const [selectedRowIndex, setSelectedRowIndex] = useState<
    number | undefined
  >(undefined);

  // Close drawer on Escape and prevent navigation
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();
        setIsOpen(false);
        setSelectedRecipientId(undefined);
        setSelectedRowIndex(undefined);
      }
    };
    window.addEventListener("keydown", handleEsc, {
      capture: true,
      passive: false,
    });
    return () => {
      window.removeEventListener("keydown", handleEsc, {
        capture: true,
      });
    };
  }, [isOpen]);

  const handleCloseDrawer = useCallback(
    (recipientId?: API.RecipientId, rowIndex?: number) => {
      const recipient = recipientId
        ? recipientsRef.current.find((r) => r.id === recipientId)
        : null;

      if (recipient && !recipient.fedwire_destination) {
        sendNotification({
          type: "negative",
          text: l10n.getString(
            "wire-recipient-drawer-missing-wire-details"
          ),
        });
      } else if (recipient && typeof rowIndex !== "undefined") {
        onSelectRecipient(rowIndex, recipient);
      }

      setSelectedRowIndex(undefined);
      setSelectedRecipientId(undefined);
      setIsOpen(false);
    },
    [sendNotification, l10n, onSelectRecipient]
  );

  const handleClickEditRecipient = useCallback(
    (recipientId: API.RecipientId, rowIndex: number) => {
      setSelectedRowIndex(rowIndex);
      setSelectedRecipientId(recipientId);
      setDrawerType(DRAWER_TYPES.edit);
      setIsOpen(true);
    },
    []
  );

  const handleClickAddRecipient = useCallback((rowIndex: number) => {
    setSelectedRowIndex(rowIndex);
    setDrawerType(DRAWER_TYPES.add);
    setIsOpen(true);
  }, []);

  return {
    isOpen,
    drawerType,
    selectedRecipientId,
    selectedRowIndex,
    handleCloseDrawer,
    handleClickEditRecipient,
    handleClickAddRecipient,
  };
};

export default useWireRecipientDrawerState;
